import * as RxDialog from '@radix-ui/react-dialog';
import { useState } from 'react';

import { BrowseFeedbackSentiment } from '@cartwhl/common/api/postBrowseFeedback';
import postBrowseFeedback from '@cartwhl/common/api/postBrowseFeedback';
import { BrowseResultItem } from '@cartwhl/common/types';
import Tooltip from '@cartwhl/ui/atoms/Tooltip';
import CrossIcon from '@cartwhl/ui/icons/CrossIcon';
import TrophyIconFilled from '@cartwhl/ui/icons/TrophyIconFilled';
import TrophyIconUnfilled from '@cartwhl/ui/icons/TrophyIconUnfilled';
import Button from '@cartwhl/ui/input/Button';

import { noticesStore } from 'stores';

type Props = {
  video: BrowseResultItem;
  category: string;
  subcategory: string;
  addToFlagged: (jobID: string) => void;
};

export default function InternalReviewIcons({
  video,
  category,
  subcategory,
  addToFlagged,
}: Props) {
  const [isFeatured, setIsFeatured] = useState(
    video.categoryAndSubcategories.includes(`${category}.featured`),
  );
  const [isDialogOpen, setIsDialogOpen] = useState(false);

  if (subcategory === undefined || subcategory === '') {
    return null;
  }
  return (
    <>
      <Tooltip text={`Add to ${category}/featured`} inModal={true}>
        <Button
          id="trophy-click"
          variant="secondary"
          size="sm"
          icon={isFeatured ? <TrophyIconFilled /> : <TrophyIconUnfilled />}
          onClick={async (e) => {
            e.stopPropagation();
            if (isFeatured) {
              noticesStore.info(
                `This motion is already in the featured category for ${category}`,
              );
              return;
            }
            const { error } = await postBrowseFeedback({
              category,
              subcategory,
              jobID: video.jobID,
              sentiment: BrowseFeedbackSentiment.ADD_TO_FEATURED,
              reason: '',
            });
            if (error) {
              console.error(error);
            } else {
              noticesStore.success(
                `Great! This motion will be added to the featured category shortly for ${category}!`,
              );
            }
          }}
        />
      </Tooltip>
      <Tooltip text={`Remove from ${category}/${subcategory}`} inModal={true}>
        <RxDialog.Root open={isDialogOpen} onOpenChange={setIsDialogOpen}>
          <Button
            id="remove-this"
            variant="secondary"
            size="sm"
            icon={<CrossIcon />}
            onClick={(e) => {
              e.stopPropagation();
              setIsDialogOpen(true);
            }}
          />
          <RxDialog.Portal>
            <RxDialog.Overlay className="fixed inset-0 bg-black/50" />
            <RxDialog.Content
              className="fixed top-1/2 left-1/2 flex w-96 -translate-x-1/2 -translate-y-1/2 flex-col items-center justify-center rounded-lg bg-white p-6 shadow-lg"
              style={{ zIndex: 50, color: 'black' }}
            >
              <RxDialog.Title className="text-center text-lg font-medium">
                Are you sure you want to remove this motion?
              </RxDialog.Title>
              <p className="text-red mt-2 text-center text-lg font-medium">
                "{video.title}"
              </p>
              <div className="mt-4 flex w-full justify-between gap-2">
                <Button
                  className="grow"
                  variant="secondary"
                  onClick={() => setIsDialogOpen(false)}
                >
                  Cancel
                </Button>
                <Button
                  className="bg-red grow text-white hover:bg-pink-600"
                  variant="tertiary-destructive"
                  onClick={async (e) => {
                    e.stopPropagation();
                    setIsDialogOpen(false);
                    addToFlagged(video.jobID);
                    const { error } = await postBrowseFeedback({
                      category,
                      jobID: video.jobID,
                      sentiment:
                        BrowseFeedbackSentiment.REMOVE_FROM_SUBCATEGORY,
                      subcategory,
                      reason: '',
                    });
                    if (error) {
                      console.error(error);
                    } else {
                      noticesStore.success(
                        `Okay! This motion will soon be removed from ${subcategory}`,
                      );
                    }
                  }}
                >
                  Remove
                </Button>
              </div>
            </RxDialog.Content>
          </RxDialog.Portal>
        </RxDialog.Root>
      </Tooltip>
    </>
  );
}
